import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Input,
  Button,
  Select,
  Slider,
  Space,
  message,
  Spin,
  Typography,
  Divider,
  Avatar,
  Tooltip,
  Empty,
  InputNumber,
  Switch,
  Collapse,
  Tabs,
  Image,
  Row,
  Col,
  Radio,
  Tag,
  Progress,
} from 'antd';
import {
  SendOutlined,
  ClearOutlined,
  RobotOutlined,
  UserOutlined,
  SettingOutlined,
  CopyOutlined,
  ReloadOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  PictureOutlined,
  SoundOutlined,
  DownloadOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  LoadingOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import llmService from '../../services/llmService';
import llmPlaygroundApi from '../../services/llmPlaygroundApi';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

// ===== Chat Mode Component =====
const ChatMode = ({ models, config, setConfig }) => {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!inputValue.trim()) {
      message.warning(t('Enter a message'));
      return;
    }

    const userMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: Date.now(),
    };

    const apiMessages = [];
    if (config.systemPrompt.trim()) {
      apiMessages.push({ role: 'system', content: config.systemPrompt.trim() });
    }
    messages.filter(msg => !msg.isError).forEach(msg => {
      apiMessages.push({ role: msg.role, content: msg.content });
    });
    apiMessages.push({ role: 'user', content: userMessage.content });

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setSending(true);

    if (config.stream) {
      try {
        abortControllerRef.current = new AbortController();
        setMessages(prev => [...prev, { role: 'assistant', content: '', timestamp: Date.now(), isStreaming: true }]);

        // 直接走 api.yeap.cloud (不经过 Cloudflare CDN，避免 SSE 被缓冲)
        const response = await fetch('https://api.yeap.cloud/llm/playground/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
          body: JSON.stringify({
            model: config.model,
            messages: apiMessages,
            temperature: config.temperature,
            max_tokens: config.maxTokens,
            top_p: config.topP,
            stream: true,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          let errMsg = '请求失败';
          try {
            const e = await response.json();
            errMsg = e.error?.message || e.error || e.message || e.msg || errMsg;
            if (typeof errMsg === 'object') errMsg = JSON.stringify(errMsg);
          } catch {}
          throw new Error(errMsg);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';
        let lineBuffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          lineBuffer += decoder.decode(value, { stream: true });
          const lines = lineBuffer.split('\n');
          lineBuffer = lines.pop(); // 保留不完整的行

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;

            // 处理 event: error
            if (trimmed.startsWith('event:')) continue;

            if (!trimmed.startsWith('data:')) continue;
            const data = trimmed.slice(5).trim();
            if (data === '[DONE]' || data === 'END') continue;

            try {
              const parsed = JSON.parse(data);
              // PHP SSE 格式: {content, finish_reason, model}
              // 也兼容 OpenAI 格式: {choices: [{delta: {content}}]}
              const content = parsed.content || parsed.choices?.[0]?.delta?.content || '';
              const finishReason = parsed.finish_reason || parsed.choices?.[0]?.finish_reason;
              const errorMsg = parsed.error;

              if (errorMsg) {
                throw new Error(typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg);
              }

              if (content) {
                fullContent += content;
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastIdx = newMessages.length - 1;
                  if (lastIdx >= 0 && newMessages[lastIdx].isStreaming) {
                    newMessages[lastIdx] = { ...newMessages[lastIdx], content: fullContent };
                  }
                  return newMessages;
                });
              }
              if (finishReason) {
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastIdx = newMessages.length - 1;
                  if (lastIdx >= 0) {
                    newMessages[lastIdx] = { ...newMessages[lastIdx], content: fullContent, isStreaming: false, model: parsed.model || config.model };
                  }
                  return newMessages;
                });
              }
            } catch (e) {
              // 忽略 JSON 解析错误（如 ping 事件或空数据）
              if (e instanceof SyntaxError) continue;
              if (e.message && !['请求失败'].includes(e.message)) throw e;
            }
          }
        }

        // 确保流结束
        setMessages(prev => {
          const newMessages = [...prev];
          const lastIdx = newMessages.length - 1;
          if (lastIdx >= 0 && newMessages[lastIdx].isStreaming) {
            newMessages[lastIdx] = { ...newMessages[lastIdx], isStreaming: false };
          }
          return newMessages;
        });

      } catch (error) {
        if (error.name === 'AbortError') {
          // 取消时移除空的 streaming placeholder
          setMessages(prev => {
            const lastIdx = prev.length - 1;
            if (lastIdx >= 0 && prev[lastIdx].isStreaming && !prev[lastIdx].content) {
              return prev.slice(0, -1);
            }
            const newMessages = [...prev];
            if (lastIdx >= 0 && newMessages[lastIdx].isStreaming) {
              newMessages[lastIdx] = { ...newMessages[lastIdx], isStreaming: false };
            }
            return newMessages;
          });
        } else {
          const errText = t(error.message || '请求失败');
          setMessages(prev => {
            const newMessages = [...prev];
            const lastIdx = newMessages.length - 1;
            if (lastIdx >= 0 && newMessages[lastIdx].isStreaming) {
              // 将 streaming placeholder 替换为错误消息
              newMessages[lastIdx] = { ...newMessages[lastIdx], content: `⚠️ ${errText}`, isStreaming: false, isError: true };
            } else {
              // 添加错误消息
              newMessages.push({ role: 'assistant', content: `⚠️ ${errText}`, isError: true });
            }
            return newMessages;
          });
        }
      } finally {
        setSending(false);
        abortControllerRef.current = null;
      }
      return;
    }

    // Non-streaming - 走 PHP 代理
    try {
      const response = await llmService.playgroundChat({
        model: config.model,
        messages: apiMessages,
        temperature: config.temperature,
        max_tokens: config.maxTokens,
        top_p: config.topP,
        stream: false,
      });

      if (response.success) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.data.content,
          usage: response.data.usage,
          model: response.data.model,
          timestamp: Date.now(),
        }]);
      } else {
        const errMsg = response.error || response.msg || response.message || t('Request failed');
        message.error(typeof errMsg === 'object' ? JSON.stringify(errMsg) : errMsg);
        setMessages(prev => prev.slice(0, -1));
      }
    } catch (error) {
      const errMsg = error.response?.data?.error?.message || error.error || error.message || t('Request failed');
      message.error(typeof errMsg === 'object' ? JSON.stringify(errMsg) : errMsg);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleClear = () => setMessages([]);

  const handleDeleteMessage = (index) => {
    setMessages(prev => prev.filter((_, i) => i !== index));
  };

  const handleCopyMessage = (content) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(content).then(() => message.success(t('Copied'))).catch(() => fallbackCopy(content));
    } else {
      fallbackCopy(content);
    }
  };

  const fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try { document.execCommand('copy'); message.success(t('Copied')); } catch { message.error(t('Failed to copy')); }
    document.body.removeChild(textarea);
  };

  const handleRegenerate = async (index) => {
    const previousMessages = messages.slice(0, index);
    const lastUserMessage = [...previousMessages].reverse().find(m => m.role === 'user');
    if (!lastUserMessage) { message.warning(t('No user messages found')); return; }

    setMessages(previousMessages);
    setSending(true);

    const apiMessages = [];
    if (config.systemPrompt.trim()) {
      apiMessages.push({ role: 'system', content: config.systemPrompt.trim() });
    }
    previousMessages.filter(msg => !msg.isError).forEach(msg => {
      apiMessages.push({ role: msg.role, content: msg.content });
    });

    try {
      const response = await llmService.playgroundChat({
        model: config.model,
        messages: apiMessages,
        temperature: config.temperature,
        max_tokens: config.maxTokens,
        top_p: config.topP,
      });

      if (response.success) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.data.content,
          usage: response.data.usage,
          model: response.data.model,
          timestamp: Date.now(),
        }]);
      } else {
        const errText = response.error || response.msg || t('Regeneration failed');
        setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${errText}`, isError: true, timestamp: Date.now() }]);
      }
    } catch (error) {
      let errMsg = error.response?.data?.error?.message || error.error || error.message || t('Regeneration failed');
      if (typeof errMsg === 'object') errMsg = JSON.stringify(errMsg);
      setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${errMsg}`, isError: true, timestamp: Date.now() }]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Card
      title={<><RobotOutlined /> {t('Chat')}</>}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}
      extra={
        <Button icon={<ClearOutlined />} onClick={handleClear} disabled={messages.length === 0}>
          {t('Empty')}
        </Button>
      }
    >
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {messages.length === 0 ? (
          <Empty description={t('Start a conversation')} style={{ marginTop: 100 }} />
        ) : (
          messages.map((msg, index) => (
            <div
              key={msg.timestamp + '-' + index}
              style={{
                display: 'flex',
                gap: 12,
                marginBottom: 16,
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
              }}
            >
              <Avatar
                icon={msg.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
                style={{
                  backgroundColor: msg.isError ? '#ff4d4f' : msg.role === 'user' ? '#1890ff' : '#52c41a',
                  flexShrink: 0,
                }}
              />
              <div
                style={{
                  maxWidth: '70%',
                  padding: '12px 16px',
                  borderRadius: 12,
                  backgroundColor: msg.isError ? '#fff2f0' : msg.role === 'user' ? '#e6f7ff' : '#f6ffed',
                  border: msg.isError ? '1px solid #ffccc7' : undefined,
                  position: 'relative',
                }}
              >
                {msg.role === 'assistant' && !msg.isError && msg.content ? (
                  // model replies are Markdown (code blocks, lists, tables); links open in a new tab, raw HTML is not rendered
                  <div className={`pg-md${msg.isStreaming ? ' typing-cursor' : ''}`}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: ({ node, ...p }) => <a {...p} target="_blank" rel="noopener noreferrer" /> }}>
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <Paragraph
                    style={{ margin: 0, whiteSpace: 'pre-wrap', color: msg.isError ? '#ff4d4f' : undefined }}
                    className={msg.isStreaming ? 'typing-cursor' : ''}
                  >
                    {msg.content || (msg.isStreaming ? '' : '...')}
                  </Paragraph>
                )}

                {msg.usage && (
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                    Tokens: {msg.usage.prompt_tokens} + {msg.usage.completion_tokens} = {msg.usage.total_tokens}
                  </Text>
                )}

                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <Tooltip title={t('Copy')}>
                    <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => handleCopyMessage(msg.content)} />
                  </Tooltip>
                  {msg.role === 'assistant' && (
                    <Tooltip title={t('Regenerate')}>
                      <Button type="text" size="small" icon={<ReloadOutlined />} onClick={() => handleRegenerate(index)} disabled={sending} />
                    </Tooltip>
                  )}
                  <Tooltip title={t('Delete')}>
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteMessage(index)} />
                  </Tooltip>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={{ padding: 16, borderTop: '1px solid #f0f0f0' }}>
        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('Enter a message. Press Enter to send, Shift+Enter for a new line')}
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={sending}
            style={{ flex: 1 }}
          />
          {sending && config.stream ? (
            <Button type="primary" danger onClick={handleStop} style={{ height: 'auto' }}>
              {t('Stop')}
            </Button>
          ) : (
            <Button type="primary" icon={<SendOutlined />} onClick={handleSend} loading={sending} style={{ height: 'auto' }}>
              {t('Send')}
            </Button>
          )}
        </Space.Compact>
      </div>
    </Card>
  );
};

// ===== Image Generation Mode Component =====
const ImageMode = ({ models }) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [images, setImages] = useState([]);
  const defaultModel = models.length > 0 ? models[0].id : 'jimeng_high_aes_general_v21_L';
  const [imageConfig, setImageConfig] = useState({
    model: defaultModel,
    size: '1024x1024',
    quality: 'standard',
    style: 'vivid',
    n: 1,
  });

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      message.warning(t('Please enter an image description'));
      return;
    }

    setGenerating(true);
    try {
      const response = await llmService.playgroundImage({
        model: imageConfig.model,
        prompt: prompt.trim(),
        size: imageConfig.size,
        quality: imageConfig.quality,
        style: imageConfig.style,
        n: imageConfig.n,
      });

      if (response.success && response.data?.data) {
        const newImages = response.data.data.map((img, i) => ({
          url: img.url || img.b64_json,
          isBase64: !!img.b64_json,
          revised_prompt: img.revised_prompt,
          timestamp: Date.now() + i,
          prompt: prompt.trim(),
          model: imageConfig.model,
        }));
        setImages(prev => [...newImages, ...prev]);
      } else {
        message.error(response.error || response.msg || response.message || t('Failed to generate image'));
      }
    } catch (error) {
      const errMsg = error.response?.data?.error || error.error || error.message || t('Failed to generate image');
      message.error(typeof errMsg === 'object' ? JSON.stringify(errMsg) : errMsg);
    } finally {
      setGenerating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <Card
      title={<><PictureOutlined /> {t('Image generation')}</>}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}
      extra={
        <Button icon={<ClearOutlined />} onClick={() => setImages([])} disabled={images.length === 0}>
          {t('Empty')}
        </Button>
      }
    >
      {/* Settings */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <Space wrap size="middle">
          <Space>
            <Text>{t('Model:')}</Text>
            <Select
              style={{ width: 180 }}
              value={imageConfig.model}
              onChange={(v) => setImageConfig(prev => ({ ...prev, model: v }))}
              showSearch
            >
              {models.length > 0 ? models.map(m => (
                <Select.Option key={m.id} value={m.id}>{m.id}</Select.Option>
              )) : (
                <>
                  <Select.Option value="jimeng_high_aes_general_v21_L">jimeng_high_aes_general_v21_L</Select.Option>
                  <Select.Option value="gemini-2.5-flash-image">gemini-2.5-flash-image</Select.Option>
                  <Select.Option value="dall-e-3">dall-e-3</Select.Option>
                </>
              )}
            </Select>
          </Space>
          <Space>
            <Text>{t('Dimensions:')}</Text>
            <Select
              style={{ width: 130 }}
              value={imageConfig.size}
              onChange={(v) => setImageConfig(prev => ({ ...prev, size: v }))}
            >
              <Select.Option value="1024x1024">1024x1024</Select.Option>
              <Select.Option value="1024x1792">1024x1792</Select.Option>
              <Select.Option value="1792x1024">1792x1024</Select.Option>
              <Select.Option value="512x512">512x512</Select.Option>
              <Select.Option value="256x256">256x256</Select.Option>
            </Select>
          </Space>
          <Space>
            <Text>{t('Quality:')}</Text>
            <Radio.Group
              value={imageConfig.quality}
              onChange={(e) => setImageConfig(prev => ({ ...prev, quality: e.target.value }))}
              size="small"
            >
              <Radio.Button value="standard">{t('Standard')}</Radio.Button>
              <Radio.Button value="hd">HD</Radio.Button>
            </Radio.Group>
          </Space>
          <Space>
            <Text>{t('Style:')}</Text>
            <Radio.Group
              value={imageConfig.style}
              onChange={(e) => setImageConfig(prev => ({ ...prev, style: e.target.value }))}
              size="small"
            >
              <Radio.Button value="vivid">{t('Vivid')}</Radio.Button>
              <Radio.Button value="natural">{t('Natural')}</Radio.Button>
            </Radio.Group>
          </Space>
        </Space>
      </div>

      {/* Image gallery */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {images.length === 0 ? (
          <Empty description={t('Enter a description to generate an image')} style={{ marginTop: 100 }} />
        ) : (
          <Row gutter={[16, 16]}>
            {images.map((img) => (
              <Col key={img.timestamp} xs={24} sm={12} md={8}>
                <Card
                  size="small"
                  cover={
                    <Image
                      src={img.isBase64 ? `data:image/png;base64,${img.url}` : img.url}
                      alt={img.prompt}
                      style={{ objectFit: 'contain' }}
                    />
                  }
                  actions={[
                    <Tooltip title={t('Download')} key="download">
                      <a href={img.isBase64 ? `data:image/png;base64,${img.url}` : img.url} download target="_blank" rel="noreferrer">
                        <DownloadOutlined />
                      </a>
                    </Tooltip>,
                    <Tooltip title={t('Copy description')} key="copy">
                      <CopyOutlined onClick={() => {
                        navigator.clipboard?.writeText(img.revised_prompt || img.prompt);
                        message.success(t('Copied'));
                      }} />
                    </Tooltip>,
                  ]}
                >
                  <Text ellipsis style={{ fontSize: 12 }}>{img.revised_prompt || img.prompt}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 11 }}>{img.model}</Text>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>

      {/* Input */}
      <div style={{ padding: 16, borderTop: '1px solid #f0f0f0' }}>
        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('Describe the image you want, e.g., a Shiba Inu wearing sunglasses surfing at the beach')}
            autoSize={{ minRows: 1, maxRows: 3 }}
            disabled={generating}
            style={{ flex: 1 }}
          />
          <Button
            type="primary"
            icon={<PictureOutlined />}
            onClick={handleGenerate}
            loading={generating}
            style={{ height: 'auto' }}
          >
            {t('Generate')}
          </Button>
        </Space.Compact>
      </div>
    </Card>
  );
};

// ===== Audio/Music Mode Component (TTS + Suno) =====
const AudioMode = ({ models }) => {
  const { t } = useTranslation();
  const [subMode, setSubMode] = useState('tts'); // 'tts' or 'music'

  // === TTS State ===
  const [inputText, setInputText] = useState('');
  const [ttsGenerating, setTtsGenerating] = useState(false);
  const [audioList, setAudioList] = useState([]);
  const [playingIndex, setPlayingIndex] = useState(-1);
  const audioRef = useRef(null);
  const [audioConfig, setAudioConfig] = useState({
    model: 'tts-1',
    voice: 'alloy',
    speed: 1.0,
  });

  // === Suno Music State ===
  const [musicGenerating, setMusicGenerating] = useState(false);
  const [musicTasks, setMusicTasks] = useState([]);
  const [musicPlayingId, setMusicPlayingId] = useState(null);
  const musicAudioRef = useRef(null);
  const musicPollingRef = useRef({});
  const [musicMode, setMusicMode] = useState('simple'); // 'simple' or 'custom'
  const [musicConfig, setMusicConfig] = useState({
    gpt_description_prompt: '',
    prompt: '',
    title: '',
    tags: '',
    make_instrumental: false,
  });

  // models are already pre-filtered as audio category from parent
  const ttsModels = models.filter(m => m.id && (m.id.includes('tts') || m.id.includes('speech')));
  const sunoModels = models.filter(m => m.id && m.id.includes('suno'));

  const voices = [
    { value: 'alloy', label: 'Alloy' },
    { value: 'echo', label: 'Echo' },
    { value: 'fable', label: 'Fable' },
    { value: 'onyx', label: 'Onyx' },
    { value: 'nova', label: 'Nova' },
    { value: 'shimmer', label: 'Shimmer' },
  ];

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      Object.values(musicPollingRef.current).forEach(clearInterval);
    };
  }, []);

  // === TTS handlers ===
  const handleTtsGenerate = async () => {
    if (!inputText.trim()) {
      message.warning(t('Enter the text to convert'));
      return;
    }

    setTtsGenerating(true);
    try {
      const blob = await llmService.playgroundAudioSpeech({
        model: audioConfig.model,
        input: inputText.trim(),
        voice: audioConfig.voice,
        speed: audioConfig.speed,
      });

      // If the backend returned a JSON error instead of audio, surface it
      if (blob && blob.type && blob.type.includes('application/json')) {
        let errMsg = t('Speech generation failed');
        try {
          const e = JSON.parse(await blob.text());
          errMsg = e.error?.message || e.error || e.msg || e.message || errMsg;
          if (typeof errMsg === 'object') errMsg = JSON.stringify(errMsg);
        } catch {}
        throw new Error(errMsg);
      }

      const url = URL.createObjectURL(blob);

      setAudioList(prev => [{
        url,
        text: inputText.trim(),
        voice: audioConfig.voice,
        model: audioConfig.model,
        timestamp: Date.now(),
      }, ...prev]);

      message.success(t('Speech generated'));
    } catch (error) {
      message.error(error.message || t('Speech generation failed'));
    } finally {
      setTtsGenerating(false);
    }
  };

  const handlePlay = (index) => {
    if (playingIndex === index) {
      audioRef.current?.pause();
      setPlayingIndex(-1);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(audioList[index].url);
    audioRef.current = audio;
    audio.play();
    setPlayingIndex(index);
    audio.onended = () => setPlayingIndex(-1);
  };

  const handleDownload = (item) => {
    const a = document.createElement('a');
    a.href = item.url;
    a.download = `tts-${item.voice}-${Date.now()}.mp3`;
    a.click();
  };

  // === Suno Music handlers ===
  const startMusicPolling = (taskId) => {
    if (musicPollingRef.current[taskId]) return;

    const interval = setInterval(async () => {
      try {
        const response = await llmService.playgroundSunoStatus(taskId);
        if (response.success && response.data) {
          const taskData = response.data;
          setMusicTasks(prev => prev.map(t => {
            if (t.taskId !== taskId) return t;
            const status = taskData.status || t.status;
            const data = taskData.data || t.data;
            const failReason = taskData.fail_reason || t.failReason;

            if (['success', 'failed', 'SUCCESS', 'FAILURE'].includes(status)) {
              clearInterval(musicPollingRef.current[taskId]);
              delete musicPollingRef.current[taskId];
            }

            return { ...t, status, data, failReason };
          }));
        }
      } catch (error) {
        // Keep polling on errors
      }
    }, 5000);

    musicPollingRef.current[taskId] = interval;
  };

  const handleMusicGenerate = async () => {
    const payload = { action: 'MUSIC' };

    if (musicMode === 'simple') {
      if (!musicConfig.gpt_description_prompt.trim()) {
        message.warning(t('Enter a music description'));
        return;
      }
      payload.gpt_description_prompt = musicConfig.gpt_description_prompt.trim();
    } else {
      if (!musicConfig.prompt.trim()) {
        message.warning(t('Enter the lyrics'));
        return;
      }
      payload.prompt = musicConfig.prompt.trim();
      if (musicConfig.title.trim()) payload.title = musicConfig.title.trim();
      if (musicConfig.tags.trim()) payload.tags = musicConfig.tags.trim();
    }

    payload.make_instrumental = musicConfig.make_instrumental;

    setMusicGenerating(true);
    try {
      const response = await llmService.playgroundSunoSubmit(payload);
      if (response.success && response.data) {
        const taskId = response.data.task_id || response.data.data || response.data.id;
        if (taskId) {
          const newTask = {
            taskId,
            description: musicMode === 'simple' ? musicConfig.gpt_description_prompt.trim() : musicConfig.title.trim() || t('Custom music'),
            status: 'submitted',
            data: null,
            failReason: null,
            timestamp: Date.now(),
          };
          setMusicTasks(prev => [newTask, ...prev]);
          startMusicPolling(taskId);
          message.success(t('Music generation task submitted'));
        } else {
          message.error(t('Unable to retrieve task ID'));
        }
      } else {
        message.error(response.error || response.msg || t('Failed to submit music generation task'));
      }
    } catch (error) {
      message.error(error.error || error.msg || error.message || t('Failed to submit music generation task'));
    } finally {
      setMusicGenerating(false);
    }
  };

  const handleMusicPlay = (audioUrl, songId) => {
    if (musicPlayingId === songId) {
      musicAudioRef.current?.pause();
      setMusicPlayingId(null);
      return;
    }
    if (musicAudioRef.current) musicAudioRef.current.pause();
    const audio = new Audio(audioUrl);
    musicAudioRef.current = audio;
    audio.play();
    setMusicPlayingId(songId);
    audio.onended = () => setMusicPlayingId(null);
  };

  const getMusicStatusTag = (status) => {
    const s = (status || '').toLowerCase();
    if (['success', 'completed'].includes(s)) return <Tag color="success">{t('Done')}</Tag>;
    if (['failure', 'failed'].includes(s)) return <Tag color="error">{t('Failed')}</Tag>;
    if (['submitted', 'queued', 'queueing'].includes(s)) return <Tag color="default">{t('Queued')}</Tag>;
    if (['processing', 'running'].includes(s)) return <Tag color="processing">{t('Generating')}</Tag>;
    return <Tag>{status}</Tag>;
  };

  // Parse songs from Suno task data
  const parseSongs = (data) => {
    if (!data) return [];
    // data could be a JSON string or an array of song objects
    let parsed = data;
    if (typeof data === 'string') {
      try { parsed = JSON.parse(data); } catch { return []; }
    }
    if (Array.isArray(parsed)) return parsed;
    if (parsed.audio_url || parsed.video_url) return [parsed];
    return [];
  };

  return (
    <Card
      title={<><SoundOutlined /> {t('Speech & music')}</>}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}
      extra={
        <Button
          icon={<ClearOutlined />}
          onClick={() => {
            if (subMode === 'tts') { setAudioList([]); setPlayingIndex(-1); }
            else { setMusicTasks([]); Object.values(musicPollingRef.current).forEach(clearInterval); musicPollingRef.current = {}; setMusicPlayingId(null); }
          }}
          disabled={subMode === 'tts' ? audioList.length === 0 : musicTasks.length === 0}
        >
          {t('Empty')}
        </Button>
      }
    >
      {/* Sub-mode tabs */}
      <div style={{ padding: '4px 16px 0', borderBottom: '1px solid #f0f0f0' }}>
        <Tabs
          activeKey={subMode}
          onChange={setSubMode}
          size="small"
          items={[
            { key: 'tts', label: t('Text to speech (TTS)') },
            { key: 'music', label: t('Music generation (Suno)') },
          ]}
          style={{ marginBottom: 0 }}
        />
      </div>

      {subMode === 'tts' ? (
        <>
          {/* TTS Settings */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
            <Space wrap size="middle">
              <Space>
                <Text>{t('Model:')}</Text>
                <Select
                  style={{ width: 140 }}
                  value={audioConfig.model}
                  onChange={(v) => setAudioConfig(prev => ({ ...prev, model: v }))}
                >
                  {ttsModels.length > 0 ? ttsModels.map(m => (
                    <Select.Option key={m.id} value={m.id}>{m.id}</Select.Option>
                  )) : (
                    <>
                      <Select.Option value="tts-1">tts-1</Select.Option>
                      <Select.Option value="tts-1-hd">tts-1-hd</Select.Option>
                    </>
                  )}
                </Select>
              </Space>
              <Space>
                <Text>{t('Voice:')}</Text>
                <Select
                  style={{ width: 120 }}
                  value={audioConfig.voice}
                  onChange={(v) => setAudioConfig(prev => ({ ...prev, voice: v }))}
                >
                  {voices.map(v => (
                    <Select.Option key={v.value} value={v.value}>{v.label}</Select.Option>
                  ))}
                </Select>
              </Space>
              <Space>
                <Text>{t('Speech speed: {{speed}}x', { speed: audioConfig.speed })}</Text>
                <Slider
                  min={0.25}
                  max={4.0}
                  step={0.25}
                  value={audioConfig.speed}
                  onChange={(v) => setAudioConfig(prev => ({ ...prev, speed: v }))}
                  style={{ width: 120 }}
                />
              </Space>
            </Space>
          </div>

          {/* TTS Audio list */}
          <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
            {audioList.length === 0 ? (
              <Empty description={t('Enter text to generate speech')} style={{ marginTop: 100 }} />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {audioList.map((item, index) => (
                  <Card key={item.timestamp} size="small">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Button
                        type="primary"
                        shape="circle"
                        icon={playingIndex === index ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                        onClick={() => handlePlay(index)}
                      />
                      <div style={{ flex: 1 }}>
                        <Paragraph ellipsis={{ rows: 2 }} style={{ margin: 0 }}>{item.text}</Paragraph>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {item.model} / {item.voice}
                        </Text>
                      </div>
                      <Tooltip title={t('Download')}>
                        <Button type="text" icon={<DownloadOutlined />} onClick={() => handleDownload(item)} />
                      </Tooltip>
                    </div>
                  </Card>
                ))}
              </Space>
            )}
          </div>

          {/* TTS Input */}
          <div style={{ padding: 16, borderTop: '1px solid #f0f0f0' }}>
            <Space.Compact style={{ width: '100%' }}>
              <TextArea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={t('Enter the text to convert to speech')}
                autoSize={{ minRows: 1, maxRows: 4 }}
                disabled={ttsGenerating}
                style={{ flex: 1 }}
              />
              <Button
                type="primary"
                icon={<SoundOutlined />}
                onClick={handleTtsGenerate}
                loading={ttsGenerating}
                style={{ height: 'auto' }}
              >
                {t('Generate')}
              </Button>
            </Space.Compact>
          </div>
        </>
      ) : (
        <>
          {/* Suno Music Settings */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Space>
                <Radio.Group value={musicMode} onChange={(e) => setMusicMode(e.target.value)} size="small">
                  <Radio.Button value="simple">{t('Simple mode')}</Radio.Button>
                  <Radio.Button value="custom">{t('Custom mode')}</Radio.Button>
                </Radio.Group>
                <Space>
                  <Switch
                    checked={musicConfig.make_instrumental}
                    onChange={(v) => setMusicConfig(prev => ({ ...prev, make_instrumental: v }))}
                    size="small"
                  />
                  <Text style={{ fontSize: 12 }}>{t('Instrumental (no vocals)')}</Text>
                </Space>
              </Space>
            </Space>
          </div>

          {/* Suno Music task list */}
          <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
            {musicTasks.length === 0 ? (
              <Empty description={musicMode === 'simple' ? t('Describe the music style you want') : t('Enter lyrics and tags to generate music')} style={{ marginTop: 80 }} />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {musicTasks.map((task) => {
                  const songs = parseSongs(task.data);
                  return (
                    <Card key={task.taskId} size="small">
                      <div style={{ marginBottom: 8 }}>
                        {getMusicStatusTag(task.status)}
                        <Text style={{ marginLeft: 8, fontWeight: 500 }}>{task.description}</Text>
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                          Task: {task.taskId}
                        </Text>
                      </div>
                      {task.failReason && (
                        <Text type="danger" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>{task.failReason}</Text>
                      )}
                      {songs.length > 0 && (
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                          {songs.map((song, idx) => (
                            <div key={song.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderTop: idx > 0 ? '1px solid #f5f5f5' : 'none' }}>
                              {song.image_url && (
                                <img src={song.image_url} alt="" style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover' }} />
                              )}
                              <Button
                                type="primary"
                                shape="circle"
                                size="small"
                                icon={musicPlayingId === (song.id || `${task.taskId}-${idx}`) ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                                onClick={() => handleMusicPlay(song.audio_url, song.id || `${task.taskId}-${idx}`)}
                                disabled={!song.audio_url}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <Text strong style={{ fontSize: 13 }}>{song.title || `Track ${idx + 1}`}</Text>
                                {song.tags && <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>{song.tags}</Text>}
                                {song.text && (
                                  <Paragraph ellipsis={{ rows: 2 }} style={{ margin: 0, fontSize: 12, color: '#666' }}>{song.text}</Paragraph>
                                )}
                              </div>
                              <Space size="small">
                                {song.audio_url && (
                                  <Tooltip title={t('Download audio')}>
                                    <a href={song.audio_url} download target="_blank" rel="noreferrer">
                                      <Button type="text" size="small" icon={<DownloadOutlined />} />
                                    </a>
                                  </Tooltip>
                                )}
                                {song.video_url && (
                                  <Tooltip title={t('Download video')}>
                                    <a href={song.video_url} download target="_blank" rel="noreferrer">
                                      <Button type="text" size="small" icon={<VideoCameraOutlined />} />
                                    </a>
                                  </Tooltip>
                                )}
                              </Space>
                            </div>
                          ))}
                        </Space>
                      )}
                    </Card>
                  );
                })}
              </Space>
            )}
          </div>

          {/* Suno Music Input */}
          <div style={{ padding: 16, borderTop: '1px solid #f0f0f0' }}>
            {musicMode === 'simple' ? (
              <Space.Compact style={{ width: '100%' }}>
                <TextArea
                  value={musicConfig.gpt_description_prompt}
                  onChange={(e) => setMusicConfig(prev => ({ ...prev, gpt_description_prompt: e.target.value }))}
                  placeholder={t('Describe the music you want, e.g., an upbeat pop song about the seaside in summer')}
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  disabled={musicGenerating}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<SoundOutlined />}
                  onClick={handleMusicGenerate}
                  loading={musicGenerating}
                  style={{ height: 'auto' }}
                >
                  {t('Generate')}
                </Button>
              </Space.Compact>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Row gutter={8}>
                  <Col span={12}>
                    <Input
                      value={musicConfig.title}
                      onChange={(e) => setMusicConfig(prev => ({ ...prev, title: e.target.value }))}
                      placeholder={t('Track title')}
                      disabled={musicGenerating}
                    />
                  </Col>
                  <Col span={12}>
                    <Input
                      value={musicConfig.tags}
                      onChange={(e) => setMusicConfig(prev => ({ ...prev, tags: e.target.value }))}
                      placeholder={t('Style tags, e.g. pop, rock, chinese')}
                      disabled={musicGenerating}
                    />
                  </Col>
                </Row>
                <Space.Compact style={{ width: '100%' }}>
                  <TextArea
                    value={musicConfig.prompt}
                    onChange={(e) => setMusicConfig(prev => ({ ...prev, prompt: e.target.value }))}
                    placeholder={t('Enter lyrics...')}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    disabled={musicGenerating}
                    style={{ flex: 1 }}
                  />
                  <Button
                    type="primary"
                    icon={<SoundOutlined />}
                    onClick={handleMusicGenerate}
                    loading={musicGenerating}
                    style={{ height: 'auto' }}
                  >
                    {t('Generate')}
                  </Button>
                </Space.Compact>
              </Space>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

// ===== Video Generation Mode Component =====
const VideoMode = ({ models }) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [tasks, setTasks] = useState([]);
  const pollingRef = useRef({});
  const defaultVideoModel = models.length > 0 ? models[0].id : 'sora';
  const [videoConfig, setVideoConfig] = useState({
    model: defaultVideoModel,
    size: '1280x720',
    seconds: '4',
  });

  // models are already pre-filtered as video category from parent
  const videoModels = models;

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingRef.current).forEach(clearInterval);
    };
  }, []);

  const startPolling = (taskId) => {
    if (pollingRef.current[taskId]) return;

    const interval = setInterval(async () => {
      try {
        const response = await llmService.playgroundVideoStatus(taskId);
        if (response.success && response.data) {
          const taskData = response.data;
          setTasks(prev => prev.map(t => {
            if (t.taskId !== taskId) return t;
            const status = taskData.status || t.status;
            const hasResult = taskData.result_url || taskData.data?.url || taskData.data?.video_url;
            const progress = taskData.progress || t.progress;
            const failReason = taskData.fail_reason || t.failReason;

            // 使用 llmPlaygroundApi 获取视频 blob
            let resultUrl = t.resultUrl;
            if (hasResult && !t.resultUrl) {
              llmPlaygroundApi.videoContent(taskId).then(blobUrl => {
                setTasks(prev2 => prev2.map(t2 =>
                  t2.taskId === taskId ? { ...t2, resultUrl: blobUrl } : t2
                ));
              }).catch(() => {});
            }

            if (['SUCCESS', 'FAILURE', 'success', 'failed', 'completed'].includes(status)) {
              clearInterval(pollingRef.current[taskId]);
              delete pollingRef.current[taskId];
            }

            return { ...t, status, resultUrl, progress, failReason };
          }));
        }
      } catch (error) {
        // Keep polling on errors
      }
    }, 5000);

    pollingRef.current[taskId] = interval;
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      message.warning(t('Enter a video description'));
      return;
    }

    setGenerating(true);
    try {
      const response = await llmService.playgroundVideo({
        model: videoConfig.model,
        prompt: prompt.trim(),
        size: videoConfig.size,
        seconds: videoConfig.seconds,
      });

      if (response.success && response.data) {
        const taskId = response.data.task_id || response.data.id || response.data.data?.task_id;
        if (taskId) {
          const newTask = {
            taskId,
            prompt: prompt.trim(),
            model: videoConfig.model,
            status: response.data.status || 'PENDING',
            resultUrl: null,
            progress: '0%',
            failReason: null,
            timestamp: Date.now(),
          };
          setTasks(prev => [newTask, ...prev]);
          startPolling(taskId);
          message.success(t('Video generation task submitted'));
        } else {
          message.error(t('Unable to retrieve task ID'));
        }
      } else {
        message.error(response.error || response.msg || t('Failed to submit video generation task'));
      }
    } catch (error) {
      message.error(error.error || error.msg || error.message || t('Failed to submit video generation task'));
    } finally {
      setGenerating(false);
    }
  };

  const getStatusTag = (status) => {
    const s = (status || '').toLowerCase();
    if (['success', 'completed'].includes(s)) return <Tag color="success">{t('Done')}</Tag>;
    if (['failure', 'failed'].includes(s)) return <Tag color="error">{t('Failed')}</Tag>;
    if (['pending', 'queued', 'submitted'].includes(s)) return <Tag color="default">{t('Queued')}</Tag>;
    if (['processing', 'running', 'in_progress'].includes(s)) return <Tag color="processing">{t('Generating')}</Tag>;
    return <Tag>{status}</Tag>;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <Card
      title={<><VideoCameraOutlined /> {t('Video generation')}</>}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}
      extra={
        <Button icon={<ClearOutlined />} onClick={() => { setTasks([]); Object.values(pollingRef.current).forEach(clearInterval); pollingRef.current = {}; }} disabled={tasks.length === 0}>
          {t('Empty')}
        </Button>
      }
    >
      {/* Settings */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <Space wrap size="middle">
          <Space>
            <Text>{t('Model:')}</Text>
            <Select
              style={{ width: 180 }}
              value={videoConfig.model}
              onChange={(v) => setVideoConfig(prev => ({ ...prev, model: v }))}
              showSearch
            >
              {videoModels.length > 0 ? videoModels.map(m => (
                <Select.Option key={m.id} value={m.id}>{m.id}</Select.Option>
              )) : (
                <>
                  <Select.Option value="sora">sora</Select.Option>
                  <Select.Option value="kling-video">kling-video</Select.Option>
                  <Select.Option value="runway-gen3">runway-gen3</Select.Option>
                  <Select.Option value="luma-video">luma-video</Select.Option>
                  <Select.Option value="cogvideox">cogvideox</Select.Option>
                </>
              )}
            </Select>
          </Space>
          <Space>
            <Text>{t('Dimensions:')}</Text>
            <Select
              style={{ width: 130 }}
              value={videoConfig.size}
              onChange={(v) => setVideoConfig(prev => ({ ...prev, size: v }))}
            >
              <Select.Option value="1280x720">{t('1280x720 (landscape)')}</Select.Option>
              <Select.Option value="720x1280">{t('720x1280 (portrait)')}</Select.Option>
              <Select.Option value="1920x1080">1920x1080</Select.Option>
              <Select.Option value="1080x1920">1080x1920</Select.Option>
              <Select.Option value="1024x1024">1024x1024</Select.Option>
            </Select>
          </Space>
          <Space>
            <Text>{t('Duration:')}</Text>
            <Select
              style={{ width: 80 }}
              value={videoConfig.seconds}
              onChange={(v) => setVideoConfig(prev => ({ ...prev, seconds: v }))}
            >
              <Select.Option value="4">4s</Select.Option>
              <Select.Option value="8">8s</Select.Option>
              <Select.Option value="12">12s</Select.Option>
            </Select>
          </Space>
        </Space>
      </div>

      {/* Task list */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {tasks.length === 0 ? (
          <Empty description={t('Enter a description to generate a video')} style={{ marginTop: 100 }} />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {tasks.map((task) => (
              <Card key={task.taskId} size="small">
                <div style={{ display: 'flex', gap: 16 }}>
                  {/* Video preview or placeholder */}
                  <div style={{ width: 200, flexShrink: 0 }}>
                    {task.resultUrl ? (
                      <video
                        src={task.resultUrl}
                        controls
                        style={{ width: '100%', borderRadius: 8, background: '#000' }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: 112,
                        background: '#f5f5f5',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        {['failure', 'failed'].includes((task.status || '').toLowerCase())
                          ? <Text type="danger">{t('Generation failed')}</Text>
                          : <LoadingOutlined style={{ fontSize: 24 }} />
                        }
                      </div>
                    )}
                  </div>
                  {/* Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: 8 }}>
                      {getStatusTag(task.status)}
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>{task.model}</Text>
                    </div>
                    <Paragraph ellipsis={{ rows: 2 }} style={{ margin: 0 }}>{task.prompt}</Paragraph>
                    {task.failReason && (
                      <Text type="danger" style={{ fontSize: 12 }}>{task.failReason}</Text>
                    )}
                    {task.resultUrl && (
                      <div style={{ marginTop: 8 }}>
                        <Button size="small" icon={<DownloadOutlined />} onClick={() => {
                          const a = document.createElement('a');
                          a.href = task.resultUrl;
                          a.download = `video-${task.taskId}.mp4`;
                          a.click();
                        }}>{t('Download')}</Button>
                      </div>
                    )}
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Task: {task.taskId}
                      </Text>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </Space>
        )}
      </div>

      {/* Input */}
      <div style={{ padding: 16, borderTop: '1px solid #f0f0f0' }}>
        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('Describe the video you want, e.g., a cat playing Moonlight Sonata on the piano')}
            autoSize={{ minRows: 1, maxRows: 3 }}
            disabled={generating}
            style={{ flex: 1 }}
          />
          <Button
            type="primary"
            icon={<VideoCameraOutlined />}
            onClick={handleGenerate}
            loading={generating}
            style={{ height: 'auto' }}
          >
            {t('Generate')}
          </Button>
        </Space.Compact>
      </div>
    </Card>
  );
};

// ===== Main Playground Component =====
const Playground = () => {
  const { t } = useTranslation();
  const [allModels, setAllModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeMode, setActiveMode] = useState('chat');

  const [config, setConfig] = useState({
    model: 'Doubao-1.5-lite-32k',
    systemPrompt: t('You are a helpful assistant.'),
    temperature: 0.7,
    maxTokens: 2000,
    topP: 1,
    stream: true,
  });

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const response = await llmService.getPlaygroundModels('all');
      if (response.success) {
        const validModels = (response.data || []).filter(m => m && m.id);
        setAllModels(validModels);
        // 设置默认 chat 模型
        const chatModels = validModels.filter(m => m.category === 'chat');
        if (chatModels.length > 0) {
          const chatIds = chatModels.map(m => m.id);
          if (!chatIds.includes(config.model)) {
            setConfig(prev => ({ ...prev, model: chatModels[0].id }));
          }
        }
      }
    } catch (error) {
      message.error(t('Failed to retrieve models'));
    } finally {
      setLoading(false);
    }
  };

  // 按类别过滤模型
  const chatModels = allModels.filter(m => m.category === 'chat');
  const imageModels = allModels.filter(m => m.category === 'image');
  const audioModels = allModels.filter(m => m.category === 'audio');
  const videoModels = allModels.filter(m => m.category === 'video');

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 180px)', gap: 16 }}>
      {/* Left panel - settings */}
      <Card
        title={<><SettingOutlined /> {t('Settings')}</>}
        style={{ width: 300, flexShrink: 0, overflow: 'auto' }}
        size="small"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Mode selector */}
          <div>
            <Text strong>{t('Mode')}</Text>
            <Tabs
              activeKey={activeMode}
              onChange={setActiveMode}
              size="small"
              style={{ marginTop: 4 }}
              items={[
                { key: 'chat', label: <><RobotOutlined /> {t('Chat')}</> },
                { key: 'image', label: <><PictureOutlined /> {t('Image generation')}</> },
                { key: 'audio', label: <><SoundOutlined /> {t('Speech')}</> },
                { key: 'video', label: <><VideoCameraOutlined /> {t('Video')}</> },
              ]}
            />
          </div>

          {activeMode === 'chat' && (
            <>
              <div>
                <Text strong>{t('Model')}</Text>
                <Select
                  style={{ width: '100%', marginTop: 8 }}
                  value={config.model}
                  onChange={(value) => setConfig(prev => ({ ...prev, model: value }))}
                  showSearch
                  optionFilterProp="children"
                >
                  {chatModels.map(model => (
                    <Select.Option key={model.id} value={model.id}>
                      {model.id}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <div>
                <Text strong>{t('System prompt')}</Text>
                <TextArea
                  style={{ marginTop: 8 }}
                  rows={4}
                  value={config.systemPrompt}
                  onChange={(e) => setConfig(prev => ({ ...prev, systemPrompt: e.target.value }))}
                  placeholder={t('Set the AI\'s role and behavior...')}
                />
              </div>

              <div>
                <Space>
                  <Text strong>{t('Streaming output')}</Text>
                  <Switch
                    checked={config.stream}
                    onChange={(checked) => setConfig(prev => ({ ...prev, stream: checked }))}
                    checkedChildren={<ThunderboltOutlined />}
                    unCheckedChildren={t('Off')}
                  />
                </Space>
              </div>

              <Collapse ghost>
                <Panel header={t('Advanced parameters')} key="advanced">
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <div>
                      <Text>Temperature: {config.temperature}</Text>
                      <Slider
                        min={0}
                        max={2}
                        step={0.1}
                        value={config.temperature}
                        onChange={(value) => setConfig(prev => ({ ...prev, temperature: value }))}
                      />
                    </div>
                    <div>
                      <Text>Max Tokens</Text>
                      <InputNumber
                        style={{ width: '100%', marginTop: 4 }}
                        min={1}
                        max={128000}
                        value={config.maxTokens}
                        onChange={(value) => setConfig(prev => ({ ...prev, maxTokens: value }))}
                      />
                    </div>
                    <div>
                      <Text>Top P: {config.topP}</Text>
                      <Slider
                        min={0}
                        max={1}
                        step={0.1}
                        value={config.topP}
                        onChange={(value) => setConfig(prev => ({ ...prev, topP: value }))}
                      />
                    </div>
                  </Space>
                </Panel>
              </Collapse>
            </>
          )}
        </Space>
      </Card>

      {/* Right panel - main content */}
      {activeMode === 'chat' && <ChatMode models={chatModels} config={config} setConfig={setConfig} />}
      {activeMode === 'image' && <ImageMode models={imageModels} />}
      {activeMode === 'audio' && <AudioMode models={audioModels} />}
      {activeMode === 'video' && <VideoMode models={videoModels} />}

      {/* Typing cursor style */}
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        .pg-md { line-height: 1.65; overflow-wrap: anywhere; }
        .pg-md > :first-child { margin-top: 0; }
        .pg-md > :last-child { margin-bottom: 0; }
        .pg-md p, .pg-md ul, .pg-md ol, .pg-md pre, .pg-md table, .pg-md blockquote { margin: 0 0 10px; }
        .pg-md ul, .pg-md ol { padding-left: 22px; }
        .pg-md h1, .pg-md h2, .pg-md h3, .pg-md h4 { margin: 14px 0 8px; font-weight: 600; line-height: 1.35; }
        .pg-md h1 { font-size: 1.3em; } .pg-md h2 { font-size: 1.2em; } .pg-md h3, .pg-md h4 { font-size: 1.05em; }
        .pg-md code { background: rgba(0,0,0,0.06); padding: 1px 5px; border-radius: 4px; font-size: 0.9em; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
        .pg-md pre { background: #1f2329; color: #e6e6e6; padding: 12px 14px; border-radius: 8px; overflow-x: auto; }
        .pg-md pre code { background: none; padding: 0; color: inherit; font-size: 13px; }
        .pg-md table { border-collapse: collapse; display: block; overflow-x: auto; }
        .pg-md th, .pg-md td { border: 1px solid rgba(0,0,0,0.12); padding: 4px 10px; }
        .pg-md blockquote { border-left: 3px solid rgba(0,0,0,0.15); padding-left: 10px; color: rgba(0,0,0,0.6); }
        .pg-md.typing-cursor > :last-child::after { content: '▋'; animation: blink 1s infinite; margin-left: 2px; }
        .pg-md.typing-cursor::after { content: none; }
        .typing-cursor::after {
          content: '▋';
          animation: blink 1s infinite;
          margin-left: 2px;
        }
      `}</style>
    </div>
  );
};

export default Playground;
